import { ReactNode, useCallback } from 'react';

import classNames from 'classnames';
import Fade from '@mui/material/Fade';

import { Box } from '@vk-hr-tek/ui/Box';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Link } from '@vk-hr-tek/ui/Link';

import { useDispatch, useSelector } from '@app/hooks';

import { useUrlWithPrefix } from '../../hooks';
import { TIMEOUT_FOR_FADE } from '../../constants';
import {
  selectSidebarState,
  selectSideMenu,
  setSidebarState,
} from '../../slice';

import { SidebarContext } from './SidebarContext';
import { SidebarMappedItem } from './SidebarMappedItem';
import { useStyles } from './Sidebar.styles';

const SIDEBAR_EXPAND_BUTTON_CLOSED_WIDTH = 40;

const SIDEBAR_EXPAND_BUTTON_OPENED_WIDTH = 256;

interface SidebarProps {
  logo: ReactNode;
  onAction?: (pageTitle: string) => void;
}

export const Sidebar = ({ logo, onAction = () => {} }: SidebarProps) => {
  const dispatch = useDispatch();
  const urlWithPrefix = useUrlWithPrefix();

  const sidebarOpened = useSelector(selectSidebarState);
  const sideMenu = useSelector(selectSideMenu);

  const sidebarIconWidth = sidebarOpened
    ? SIDEBAR_EXPAND_BUTTON_OPENED_WIDTH
    : SIDEBAR_EXPAND_BUTTON_CLOSED_WIDTH;

  const { classes } = useStyles({
    sidebarIconWidth,
  });

  const handleSidebarOpen = useCallback(
    (value: boolean) => {
      dispatch(setSidebarState(value));
    },
    [dispatch],
  );

  const handleIsActive = (path: string, href: string): boolean => {
    if (href === '/') {
      return path === href;
    }
    if (href === '/settings') {
      return (
        path === href ||
        path.includes('/event-types') ||
        path.includes('/settings')
      );
    }
    return (
      path === href ||
      path === `/employee${href}` ||
      path.startsWith(`${href}/`) ||
      path.startsWith(`/employee${href}/`)
    );
  };

  const handleSidebarToggle = () => handleSidebarOpen(!sidebarOpened);

  return (
    <Box
      className={classNames(
        classes.sidebar,
        'aqa_sidebar',
        sidebarOpened && classes.sidebarOpened,
      )}
    >
      <Box className={classNames(classes.sidebarLogo, 'aqa_sidebarLogo')}>
        <Link {...urlWithPrefix('/')}>{logo}</Link>
      </Box>

      <Box className={classes.overlayTop} />

      <Box className={classNames(classes.sidebarMenu, 'aqa_sidebarMenu')}>
        <SidebarContext
          value={{
            sidebarOpened,
            isActive: handleIsActive,
            onMenuItemClick: onAction,
          }}
        >
          {sideMenu?.map((item) => (
            <SidebarMappedItem key={item.id} item={item} />
          ))}
        </SidebarContext>
      </Box>

      <Box className={classes.overlayBottom} />

      <Box
        className={classNames(
          classes.sideBarExpandButton,
          'aqa_sidebarExpandButton',
        )}
        onClick={handleSidebarToggle}
      >
        <Tooltip
          placement="right"
          title="Развернуть меню"
          disabled={sidebarOpened}
        >
          <Box
            minWidth="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="aqa_sidebarExpandIcon"
          >
            <Icon
              name={sidebarOpened ? 'chevron_left_2' : 'chevron_right_2'}
              size="20"
              color="text.light.tertirary"
            />
          </Box>
        </Tooltip>

        <Fade in={sidebarOpened} timeout={TIMEOUT_FOR_FADE}>
          <Box
            width="100%"
            mr="auto"
            height="20px"
            className="aqa_sidebarExpandText"
          >
            <Typography variant="body3" color="text.light.tertirary" noWrap>
              Скрыть меню
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};
